import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";

interface GeneratePdfResponse {
  success: boolean;
  message?: string;
  pdfBase64?: string;
  fileName?: string;
}

const DownloadPdfButton = ({
  disabledFromParent,
  fileName,
  htmlContent,
}: {
  disabledFromParent: boolean;
  fileName: string;
  htmlContent: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    if (!htmlContent || htmlContent.trim() === "") {
      toast.error("אין תוכן להורדה");
      return;
    }

    setIsLoading(true);

    try {
      // Call Firebase function
      const generatePdfFn = httpsCallable<{ html: string; fileName?: string }, GeneratePdfResponse>(
        functions,
        "generatePdfFromHtml"
      );

      const result = await generatePdfFn({
        html: htmlContent,
        fileName: fileName,
      });

      if (!result.data.success) {
        throw new Error(result.data.message || "Failed to generate PDF");
      }

      if (!result.data.pdfBase64) {
        throw new Error("PDF data not returned");
      }

      // Convert base64 to blob
      const binaryString = atob(result.data.pdfBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "application/pdf" });

      // Trigger download
      const downloadFileName = result.data.fileName || fileName;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${downloadFileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("PDF הורד בהצלחה");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(error instanceof Error ? error.message : "שגיאה ביצירת ה-PDF");
    } finally {
      setIsLoading(false);
    }
  };

  const label = isLoading ? "יוצר PDF..." : "הורד כPDF";

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={handleDownload} 
      disabled={isLoading || disabledFromParent}
    >
      {isLoading ? <Spinner className="h-4 w-4" /> : <Download className="h-4 w-4" />}
      {label}
    </Button>
  );
};

export default DownloadPdfButton;
